import React from 'react';
import { motion } from 'framer-motion';
import { useLang, t, tx  } from '../context/LanguageContext';
import translations from '../i18n/translations';
import { MapPin, Award, Heart, Lightbulb, Eye, Users } from 'lucide-react';
import SEO from '../components/SEO';
import { SEO_TEXTES } from '../data/seoTextes';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const valueIcons = [Heart, Award, Eye, Lightbulb];

export default function Company() {
  const { lang } = useLang();
  const c = translations.company;

  return (
    <>
      <SEO {...SEO_TEXTES['/entreprise']} path="/entreprise" />
    <main data-testid="company-page">
      {/* Hero */}
      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-4">MÉTÉRIS</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-6">{t(c.title, lang)}</h1>
            <p className="text-xl text-slate-300 max-w-2xl">{t(c.subtitle, lang)}</p>
          </motion.div>
        </div>
      </section>

      {/* History */}
      <section className="py-24 md:py-32 bg-[#15171E]/50" data-testid="history-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-3">
                {t(c.history.title, lang)}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">
                {tx(lang, '15+ ans à vos côtés', '15+ years by your side', { es: '15+ años con usted', de: '15+ jahre mit ihnen', it: '15+ anni con te', pt: '15+ anos contigo' })}
              </h2>
              <p className="text-slate-300 leading-relaxed">{t(c.history.text, lang)}</p>
            </motion.div>
            <motion.div {...fadeUp} className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                alt={tx(lang, 'Équipe MÉTÉRIS', 'MÉTÉRIS Team', { es: 'METERIS Team', de: 'METERIS Team', it: 'Team METERIS', pt: 'Equipa METERIS' })}
                className="rounded-2xl w-full h-80 object-cover border border-white/5"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#0B0C10]/60 to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Zone */}
      <section className="py-24 md:py-32" data-testid="zone-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-10 md:p-16">
            <div className="flex items-start gap-6">
              <MapPin className="w-10 h-10 text-[#0099D6] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
                  {t(c.zone.title, lang)}
                </h2>
                <p className="text-slate-300 leading-relaxed max-w-2xl">{t(c.zone.text, lang)}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  {['Seine-et-Marne (77)', 'Val-de-Marne (94)', 'Seine-Saint-Denis (93)', 'Val-d\'Oise (95)', 'Ile-de-France'].map((zone, i) => (
                    <span key={i} className="px-3 py-1.5 rounded-full bg-white/5 text-xs font-medium text-slate-300 border border-white/10">
                      {zone}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#15171E]/50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-12">
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-3">
              {t(c.values.title, lang)}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
              {tx(lang, 'Ce qui nous définit', 'What defines us', { es: 'Lo que nos define', de: 'Was uns definiert', it: 'Cosa ci definisce', pt: 'O que nos define' })}
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {c.values.items.map((v, i) => {
              const Icon = valueIcons[i] || Heart;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8"
                >
                  <Icon className="w-8 h-8 text-[#0099D6] mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{t(v.title, lang)}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{t(v.desc, lang)}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-24 md:py-32" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-12">
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-3">
              {t(c.partners.title, lang)}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
              {tx(lang, 'Un réseau d\'excellence', 'A network of excellence', { es: 'Una red de excelencia', de: 'Ein Netzwerk von Exzellenz', it: 'Una rete di eccellenza', pt: 'Uma rede de excelência' })}
            </h2>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {c.partners.items.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-[#15171E]/80 border border-white/5 rounded-xl p-6 text-center"
              >
                <Users className="w-6 h-6 text-[#0099D6] mx-auto mb-3" />
                <h4 className="text-sm font-semibold text-white mb-1">{p.name}</h4>
                <p className="text-xs text-slate-400">{t(p.desc, lang)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
    </>
  );
}
